/**
 * Per-tenant brand config. Source of truth for white-label theming.
 * See docs/white-label-theming-plan.md.
 */

export type TenantFontSource =
  | { kind: 'google'; href: string }
  | {
      kind: 'self-hosted';
      faces: { weight: number; style: string; woff2: string }[];
    }
  | { kind: 'system' };

export type TenantLegalConfig = {
  /** Sanity pages slug; falls back to TM default when omitted. */
  termsPageSlug?: string;
  privacyPageSlug?: string;
  /** Sanity fileDocument slugs for PDF links. */
  termsFileSlug?: string;
  privacyFileSlug?: string;
};

export type TenantSurveyConfig = {
  /** Sanity pages slug for /survey/intro CMS block. Default: survey-intro */
  introPageSlug?: string;
};

export type TenantConfig = {
  id: string;
  domains: string[];
  orgName: string;
  /** Compact product name (titles, meta). */
  productName: string;
  /** Human-facing product name in sentences. */
  productNameDisplay: string;
  /**
   * Legal copyright line entity. Defaults to Terrace Metrics, Inc. when omitted —
   * white-label tenants keep TM copyright unless they explicitly override.
   */
  copyrightHolder?: string;

  logo: {
    /** 'mask' = recolourable silhouette (TM). 'image' = fixed artwork (FlowState). */
    render: 'mask' | 'image';
    /** Mark / lockup used by LogoGlyph (primary variant). */
    primary: string;
    /**
     * Self-contained lockup (no programmatic wordmark) for API/reports.
     * Empty string = not yet provided (mask tenants often defer this).
     */
    fullLogo: string;
    reverse: string;
    icon: string;
    mono?: string;
    /** width ÷ height */
    aspect: number;
    favicon: string;
    ogImage: string;
  };

  fonts: {
    sans: string;
    serif: string;
    source: TenantFontSource;
  };

  colors: {
    brand: string;
    brandDark: string;
    brandTint: string;
    brandAccent: string;
    deep: string;
    bg: string;
    bgAlt: string;
    ink: string;
    /** Flat colour or any CSS `background` value (e.g. gradient). */
    ctaBackground: string;
  };

  seo: { title: string; description: string };
  legal?: TenantLegalConfig;
  survey?: TenantSurveyConfig;
};

export const DEFAULT_TENANT = 'resiliencyforyou';

/** Used in © lines unless a tenant sets `copyrightHolder`. */
export const DEFAULT_COPYRIGHT_HOLDER = 'Terrace Metrics, Inc.';

export const DEFAULT_LEGAL = {
  termsPageSlug: 'terms-and-conditions',
  privacyPageSlug: 'privacy',
  termsFileSlug: 'terrace-metrics-terms-conditions',
  privacyFileSlug: 'terrace-metrics-data-privacy-policy',
} as const;

export const DEFAULT_SURVEY = {
  introPageSlug: 'survey-intro',
} as const;

/** Legacy ids accepted by ?tenant= / VITE_TENANT. */
const TENANT_ID_ALIASES: Record<string, string> = {
  tm: 'resiliencyforyou',
};

export const TENANTS: Record<string, TenantConfig> = {
  resiliencyforyou: {
    id: 'resiliencyforyou',
    domains: [
      'resiliencyforyou.com',
      'dev.resiliencyforyou.com',
      'www.resiliencyforyou.com',
      'localhost',
      '127.0.0.1',
    ],
    orgName: 'Terrace Metrics',
    productName: 'Terrace Metrics',
    productNameDisplay: 'Terrace Metrics (resiliencyforyou)',
    copyrightHolder: 'Terrace Metrics, Inc.',
    logo: {
      render: 'mask',
      primary: '/assets/logonotext.png',
      fullLogo: '/assets/fullLogo.png', // TODO: public lockup with wordmark for reports
      reverse: '/assets/logonotext.png',
      icon: '/assets/logonotext.png',
      aspect: 300 / 267,
      favicon: '/favicon.png',
      ogImage: '/og-preview.png',
    },
    fonts: {
      sans: 'DM Sans',
      serif: 'DM Serif Display',
      source: {
        kind: 'google',
        href: 'https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600&display=swap',
      },
    },
    colors: {
      brand: '#4a7c59',
      brandDark: '#3d6649',
      brandTint: '#f5f9f6',
      brandAccent: '#8fa590',
      deep: '#1f3527',
      bg: '#fafaf8',
      bgAlt: '#f5f0e8',
      ink: '#1a1f1c',
      ctaBackground: '#4a7c59',
    },
    seo: {
      title: 'ResiliencyForYou - Terrace Metrics',
      description: 'Wellness and Resiliency Assessment by Terrace Metrics',
    },
  },

  flowstate: {
    id: 'flowstate',
    domains: ['vendor.getflowstate.io', 'dev.getflowstate.io', 'getflowstate.io'],
    orgName: 'FlowState',
    productName: 'FlowState Resiliency',
    productNameDisplay: 'FlowState Resiliency',
    copyrightHolder: 'Terrace Metrics, Inc.',
    logo: {
      render: 'image',
      primary: '/tenants/flowstate/logo-primary.png',
      fullLogo: '/tenants/flowstate/logo-primary.png',
      reverse: '/tenants/flowstate/logo-reverse.png',
      icon: '/tenants/flowstate/logo-icon.png',
      mono: '/tenants/flowstate/logo-black.png',
      aspect: 7.3,
      favicon: '/tenants/flowstate/favicon.png',
      ogImage: '/tenants/flowstate/og.png',
    },
    fonts: {
      sans: 'Helvetica Neue',
      serif: 'Helvetica Neue',
      source: { kind: 'system' },
    },
    colors: {
      brand: '#005CB0',
      brandDark: '#011A45',
      brandTint: '#B6ECFF',
      brandAccent: '#4FB8F0',
      deep: '#01183F',
      bg: '#ffffff',
      bgAlt: '#f4fbff',
      ink: '#011A45',
      /**
       * Ends at #0E73A9, not brandAccent #4FB8F0: .btn-primary puts white text
       * on this, and white on #4FB8F0 is 2.2:1 — well under the 4.5:1 WCAG AA
       * needs. #0E73A9 keeps the accent's hue (201°) and saturation while
       * reaching 5.2:1, so the gradient still travels dark -> light.
       * Keep any future end stop at or below this lightness.
       */
      ctaBackground: 'linear-gradient(90deg, #005CB0 0%, #0E73A9 100%)',
    },
    seo: {
      title: 'FlowState — Human Factors Behind Performance',
      description:
        'FlowState measures the human factors behind performance, identifies resilience risks, and turns insight into action for leaders and teams.',
    },
    survey: {
      introPageSlug: 'survey-intro-flowstate',
    },
  },

  /** Mock white-label partner for demos / branding-sample. */
  newpartner: {
    id: 'newpartner',
    domains: ['vendor-demo.resiliencyforyou.com'],
    orgName: 'New Partner',
    productName: 'NewPartner',
    productNameDisplay: 'New Partner',
    logo: {
      render: 'image',
      primary: '/tenants/newpartner/logo-primary.svg',
      fullLogo: '/tenants/newpartner/logo-primary.svg',
      reverse: '/tenants/newpartner/logo-reverse.svg',
      icon: '/tenants/newpartner/logo-icon.svg',
      mono: '/tenants/newpartner/logo-black.svg',
      aspect: 3,
      favicon: '/tenants/newpartner/favicon.svg',
      ogImage: '/tenants/newpartner/logo-primary.svg',
    },
    fonts: {
      sans: 'Source Sans 3',
      serif: 'Source Serif 4',
      source: {
        kind: 'google',
        href: 'https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@300;400;500;600;700&family=Source+Serif+4:ital,wght@0,400;0,600;1,400&display=swap',
      },
    },
    colors: {
      brand: '#0D9488',
      brandDark: '#0F766E',
      brandTint: '#F0FDFA',
      brandAccent: '#2DD4BF',
      deep: '#134E4A',
      bg: '#FAFAFA',
      bgAlt: '#F0FDFA',
      ink: '#134E4A',
      ctaBackground: 'linear-gradient(90deg, #0D9488 0%, #2DD4BF 100%)',
    },
    seo: {
      title: 'New Partner — Resilience Demo',
      description:
        'Demo white-label tenant for showcasing partner branding on ResiliencyForYou.',
    },
  },
};

function resolveTenantId(id: string | null | undefined): TenantConfig | null {
  if (!id) return null;
  const canonical = TENANT_ID_ALIASES[id] ?? id;
  return TENANTS[canonical] ?? null;
}

/** Resolve by id, hostname, ?tenant=, or VITE_TENANT. */
export function resolveTenant(options?: {
  hostname?: string;
  search?: string;
  envTenant?: string;
}): TenantConfig {
  const search = options?.search ?? (typeof window !== 'undefined' ? window.location.search : '');
  const params = new URLSearchParams(search);
  const fromQuery = resolveTenantId(params.get('tenant'));
  if (fromQuery) return fromQuery;

  const fromEnv = resolveTenantId(
    options?.envTenant ?? (import.meta.env.VITE_TENANT as string | undefined)
  );
  if (fromEnv) return fromEnv;

  const hostname =
    options?.hostname ??
    (typeof window !== 'undefined' ? window.location.hostname : '');
  return resolveTenantByHost(hostname);
}

/** Resolve tenant by hostname. Falls back to DEFAULT_TENANT. */
export function resolveTenantByHost(hostname: string): TenantConfig {
  const host = hostname.toLowerCase().replace(/^www\./, '');
  for (const tenant of Object.values(TENANTS)) {
    if (
      tenant.domains.some((d) => d.toLowerCase().replace(/^www\./, '') === host)
    ) {
      return tenant;
    }
  }
  return TENANTS[DEFAULT_TENANT];
}

export function getTenant(id: string): TenantConfig | null {
  return resolveTenantId(id);
}

export function resolveTermsPageSlug(tenant: TenantConfig): string {
  return tenant.legal?.termsPageSlug ?? DEFAULT_LEGAL.termsPageSlug;
}

export function resolvePrivacyPageSlug(tenant: TenantConfig): string {
  return tenant.legal?.privacyPageSlug ?? DEFAULT_LEGAL.privacyPageSlug;
}

export function resolveTermsFileSlug(tenant: TenantConfig): string {
  return tenant.legal?.termsFileSlug ?? DEFAULT_LEGAL.termsFileSlug;
}

export function resolvePrivacyFileSlug(tenant: TenantConfig): string {
  return tenant.legal?.privacyFileSlug ?? DEFAULT_LEGAL.privacyFileSlug;
}

export function resolveSurveyIntroPageSlug(tenant: TenantConfig): string {
  return tenant.survey?.introPageSlug ?? DEFAULT_SURVEY.introPageSlug;
}

export function resolveCopyrightHolder(tenant: TenantConfig): string {
  return tenant.copyrightHolder ?? DEFAULT_COPYRIGHT_HOLDER;
}

/** Apply tenant colours/fonts as CSS custom properties on an element (usually <html>). */
export function applyTenantCssVars(
  tenant: TenantConfig,
  el: HTMLElement = document.documentElement
): void {
  const { colors, fonts } = tenant;
  const root = el.style;
  root.setProperty('--brand', colors.brand);
  root.setProperty('--brand-dark', colors.brandDark);
  root.setProperty('--brand-tint', colors.brandTint);
  root.setProperty('--brand-accent', colors.brandAccent);
  root.setProperty('--deep', colors.deep);
  root.setProperty('--cta-bg', colors.ctaBackground);

  // Legacy aliases used throughout components / theme.css
  root.setProperty('--tm-green', colors.brand);
  root.setProperty('--tm-green-dark', colors.brandDark);
  root.setProperty('--tm-green-light', colors.brandTint);
  root.setProperty('--tm-green-accent', colors.brandAccent);
  root.setProperty('--marble-deep', colors.deep);
  root.setProperty('--brand-primary', colors.brand);

  root.setProperty('--tm-bg', colors.bg);
  root.setProperty('--tm-bg-warm', colors.bgAlt);
  root.setProperty('--tm-ink', colors.ink);
  root.setProperty('--tm-text', colors.ink);
  root.setProperty('--background', colors.bg);
  root.setProperty('--foreground', colors.ink);
  root.setProperty('--secondary', colors.bgAlt);
  root.setProperty('--secondary-foreground', colors.ink);
  root.setProperty('--muted', colors.bgAlt);
  root.setProperty('--accent', colors.brandTint);
  root.setProperty('--accent-foreground', colors.brandDark);
  root.setProperty('--primary', colors.brand);
  root.setProperty('--header-navy', colors.deep);
  root.setProperty('--tm-dark-surface', colors.deep);

  const sansStack = `"${fonts.sans}", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif`;
  const serifStack = `"${fonts.serif}", Georgia, serif`;
  root.setProperty('--tm-font-sans', sansStack);
  root.setProperty('--tm-font-serif', serifStack);
  root.setProperty('--font-sans', sansStack);
  root.setProperty('--font-serif', serifStack);
}
